import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NicknameField } from "@/components/wallet/NicknameField";

// Shown when a verified wallet isn't linked to any account yet.
export function WalletSignupDialog({ data, onClose }) {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [nick, setNick] = useState("");
  const [name, setName] = useState("");
  const [valid, setValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    setBusy(true); setErr("");
    try {
      const r = await client.post("/auth/wallet/register", { signup_ticket: data.signup_ticket, nickname: nick, display_name: name });
      setSession(r.data.token, r.data.user);
      toast.success("Welcome to OnchainLyfe");
      onClose();
      navigate("/account");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wallet-signup-dialog" className="bg-[#0b0b0b] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">New wallet detected</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            <span className="font-mono-cl text-[#ccff00]" data-testid="wallet-signup-address">{data?.address_masked}</span> isn't linked to an OnchainLyfe account yet.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-xs text-zinc-400" data-testid="wallet-signup-existing-note">
          Already have an account with email or Google? Log in with it first, then open <b className="text-zinc-200">Account → Wallets</b> to link this wallet. That keeps your memberships and history in one place.
        </div>
        <div className="space-y-3">
          <p className="text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500">Or create a wallet-only account</p>
          <NicknameField value={nick} onChange={setNick} onValidity={setValid} testid="wallet-signup-nickname" />
          <input data-testid="wallet-signup-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Display name (optional)"
            className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" />
          {err && <p data-testid="wallet-signup-error" className="text-sm text-red-400">{err}</p>}
          <button data-testid="wallet-signup-submit" disabled={!valid || busy} onClick={create}
            className="w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-50">
            {busy ? "Creating…" : "Create account"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
